import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const CartContext = createContext(null);
const STORAGE_KEY = "ahamyog_cart_v1";
// Set to "1" whenever the local cart is mutated while the user is
// anonymous. Cleared after a successful merge / on logout. This lets
// us distinguish "genuine guest items" from "a mirror of the server
// cart persisted by a previous session" — merging the latter with the
// server side doubles every qty on every reload (the giant-badge bug).
const DIRTY_KEY = "ahamyog_cart_v1_dirty";
// Sane per-line qty ceiling — mirrored by the backend guard in
// routers/cart.py so a client that ignores this still can't poison
// the stored cart.
const MAX_QTY = 99;

function clampQty(n) {
    const v = Number.isFinite(+n) ? Math.floor(+n) : 1;
    return Math.max(1, Math.min(MAX_QTY, v));
}

function sanitizeItems(list) {
    return (list || []).map((it) => ({ ...it, qty: clampQty(it.qty) }));
}

function readLocal() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? sanitizeItems(JSON.parse(raw)) : [];
    } catch {
        return [];
    }
}

function mergeItems(a, b) {
    const map = new Map();
    [...a, ...b].forEach((it) => {
        const key = `${it.kind}:${it.id}`;
        if (map.has(key)) {
            const prev = map.get(key);
            map.set(key, { ...prev, qty: prev.qty + it.qty });
        } else {
            map.set(key, { ...it });
        }
    });
    return Array.from(map.values());
}

export function CartProvider({ children }) {
    const { user } = useAuth();
    const [items, setItems] = useState(() => readLocal());
    const [open, setOpen] = useState(false);
    const mergedRef = useRef(false);
    const lastSyncedRef = useRef("");

    // persist locally always
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }, [items]);

    // On login: merge guest items with server cart (one-shot per session)
    useEffect(() => {
        const isAuthed = user && typeof user === "object";
        if (!isAuthed) {
            mergedRef.current = false;
            return;
        }
        if (mergedRef.current) return;
        mergedRef.current = true;
        // Only merge if the local cart was actually mutated while the
        // user was anonymous. Otherwise the local list is just a
        // mirror of the server cart from a previous session and
        // merging would double every qty on every reload.
        const isDirty = localStorage.getItem(DIRTY_KEY) === "1";
        if (!isDirty) {
            api.get("/cart")
                .then(({ data }) => {
                    const clean = sanitizeItems(data || []);
                    setItems(clean);
                    lastSyncedRef.current = JSON.stringify(clean);
                })
                .catch(() => {});
            return;
        }
        const guestItems = readLocal();
        api.post("/cart/merge", { items: guestItems })
            .then(({ data }) => {
                const clean = sanitizeItems(data.items || []);
                setItems(clean);
                lastSyncedRef.current = JSON.stringify(clean);
                localStorage.removeItem(DIRTY_KEY);
            })
            .catch(() => {
                // fallback: try GET cart
                api.get("/cart").then(({ data }) => {
                    const merged = sanitizeItems(mergeItems(data || [], guestItems));
                    setItems(merged);
                    lastSyncedRef.current = JSON.stringify(merged);
                    localStorage.removeItem(DIRTY_KEY);
                }).catch(() => {});
            });
    }, [user]);

    // Sync subsequent changes to server when authed
    useEffect(() => {
        const isAuthed = user && typeof user === "object";
        if (!isAuthed || !mergedRef.current) return;
        const snap = JSON.stringify(items);
        if (snap === lastSyncedRef.current) return;
        const t = setTimeout(() => {
            api.post("/cart/sync", { items }).then(() => {
                lastSyncedRef.current = snap;
            }).catch(() => {});
        }, 350);
        return () => clearTimeout(t);
    }, [items, user]);

    const addItem = (item, qty = 1) => {
        const isAnon = !(user && typeof user === "object");
        if (isAnon) localStorage.setItem(DIRTY_KEY, "1");
        setItems((prev) => {
            // Courses are exclusive: one mode at a time, qty fixed at 1
            if (item.kind === "course") {
                const filtered = prev.filter((i) => !(i.id === item.id && i.kind === "course"));
                return [...filtered, { ...item, qty: 1 }];
            }
            const idx = prev.findIndex((i) => i.id === item.id && i.kind === item.kind);
            if (idx >= 0) {
                const next = [...prev];
                next[idx] = { ...next[idx], qty: clampQty(next[idx].qty + qty) };
                return next;
            }
            return [...prev, { ...item, qty: clampQty(qty) }];
        });
        setOpen(true);
    };

    const removeItem = (id, kind) => {
        const isAnon = !(user && typeof user === "object");
        if (isAnon) localStorage.setItem(DIRTY_KEY, "1");
        setItems((prev) => prev.filter((i) => !(i.id === id && i.kind === kind)));
    };

    const updateQty = (id, kind, qty) => {
        if (qty <= 0) return removeItem(id, kind);
        const isAnon = !(user && typeof user === "object");
        if (isAnon) localStorage.setItem(DIRTY_KEY, "1");
        setItems((prev) => prev.map((i) => (i.id === id && i.kind === kind ? { ...i, qty: clampQty(qty) } : i)));
    };

    const clear = () => {
        localStorage.removeItem(DIRTY_KEY);
        setItems([]);
    };

    const totals = useMemo(() => {
        const count = items.reduce((s, i) => s + i.qty, 0);
        const subtotal = items.reduce((s, i) => s + i.qty * i.price, 0);
        return { count, subtotal };
    }, [items]);

    return (
        <CartContext.Provider
            value={{ items, open, setOpen, addItem, removeItem, updateQty, clear, ...totals }}
        >
            {children}
        </CartContext.Provider>
    );
}

export function useCart() {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used within CartProvider");
    return ctx;
}
